import { createMemoStyles } from '@wds/utils';
import { PricingAppTheme } from '~app/styles/pricing/types';
import { HIGHLIGHT } from '~pricing/components/Summary/useStyles';
import { Packages } from '~shared/constants/pricing';

import {
  BETWEEN_LG_XL_MEDIA_QUERY,
  BETWEEN_MD_LG_MEDIA_QUERY,
  LG_MEDIA_QUERY,
  MD_MEDIA_QUERY,
  XL_MEDIA_QUERY,
} from '~shared/media-query';

type Props = {
  packageName: Packages;
};
type Rules = 'Highlighted' | 'Package';

export default createMemoStyles<Rules, Props, PricingAppTheme>(
  ({ spacing, pricingPalette, packageTheme }: PricingAppTheme) => ({
    Package: {
      position: 'relative',
      border: `1px solid ${pricingPalette.colors.pebble}`,
      [MD_MEDIA_QUERY]: {
        '&:not(:last-of-type):not($Highlighted)': {
          borderRight: 'none',
        },
      },
      '& aside': {
        position: 'relative',
        borderRadius: 0,
        borderBottomWidth: 3,
        borderTop: 'none',

        '&:before, &:after': {
          display: 'none',
        },

        '& p': {
          fontSize: 13,
        },

        [MD_MEDIA_QUERY]: {
          position: 'absolute',
          top: 'auto',
          bottom: '100%',
          left: -1,
          width: '100%',
          borderWidth: 3,
          borderTop: 'inherit',
        },
      },
      '& header': {
        '& h2, & h3': {
          textAlign: 'center',
        },

        [MD_MEDIA_QUERY]: {
          fontSize: 12,
          padding: [spacing[4], spacing[3]],

          '& h3': {
            '& span:last-of-type *': {
              fontSize: '1em',
            },
          },
        },

        [BETWEEN_MD_LG_MEDIA_QUERY]: {
          '& [class*=Values]': {
            minHeight: 24,
            fontSize: '.5em',
            '& span': {
              padding: [0, '3%'],
            },
          },

          '& h3': {
            minHeight: 50,
          },
        },

        [BETWEEN_LG_XL_MEDIA_QUERY]: {
          paddingLeft: spacing[3],
          paddingRight: spacing[3],

          '& [class*=Values]': {
            fontSize: '.7em',
          },
        },

        [XL_MEDIA_QUERY]: {
          fontSize: 16,
          minHeight: 300,
          maxHeight: 300,

          '& [class*=Values], & h3 span:last-of-type *, & [class*=Byline] p, & [class*=Byline] h4, & button': {
            fontSize: '.875em',
          },

          '& h3, & [class*=Byline]': {
            minHeight: 'unset',
          },

          '& h2': {
            fontSize: '1em',
            marginBottom: spacing[3],
          },

          '& h3 > span:first-of-type': {
            fontSize: '1.25em',
          },
        },

        '& [class*=Byline]': {
          justifyContent: 'center',
          '& *': {
            textAlign: 'center',
          },

          '& svg:first-child': {
            display: 'none',
          },

          '& button svg:first-child': {
            display: 'inline-block',
          },

          [MD_MEDIA_QUERY]: {
            minHeight: 90,
          },

          [LG_MEDIA_QUERY]: {
            minHeight: 70,
          },

          [XL_MEDIA_QUERY]: {
            margin: [10, 0],
            minHeight: 50,
          },
        },

        '& [class*=SelectUser]': {
          justifyContent: 'center',

          [MD_MEDIA_QUERY]: {
            minHeight: 60,

            '& > svg': {
              display: 'none',
            },
          },

          [XL_MEDIA_QUERY]: {
            marginBottom: 0,
          },
        },
      },
    },
    Highlighted: {
      ...HIGHLIGHT(pricingPalette.colors.flint, packageTheme),
      borderWidth: '3px !important',

      [MD_MEDIA_QUERY]: {
        '& header': {
          paddingTop: spacing[4] - 2,
        },
      },
    },
  })
);
